import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header';
import databg from '@/assets/products/senti.png';

import {
  Globe,
  MessageSquare,
  Phone,
  Zap,
  TrendingUp,
  Tag,
} from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/sentiment');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/sentiment-source-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/sentiment-divergence-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/news-narrative-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/catalyst-radar-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

const SentimentPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Market Sentiment API – Sentiment Analysis for Stocks"
        description="Sentiment analysis API for market sentiment data. News, social media, and analyst signals across 500+ sources with daily updates and decades of history."
        canonicalUrl="https://axionquant.com/products/sentiment"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion Market Sentiment API",
          "description": "Sentiment analysis API for market sentiment data. News, social media, and analyst signals across 500+ sources with daily updates and decades of history.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Market Sentiment API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>
     <Header
  docs="/docs/02-alternative-data/sentiment"
  title="Market Sentiment API – Quantify the Narrative"
  body="Sentiment analysis API that aggregates social, news, and analyst signals into a single quantitative view. Track stock sentiment momentum shifts, conviction divergence, and crowd-vs-analyst disagreement across any watchlist."
  crumb={{name: 'Sentiment', path:'/products/sentiment'}}
  tabs={[
    {
      label: 'Full Signal Stack',
      language: 'python',
      filename: 'full_signal_stack.py',
      bg: '14.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers = ["NVDA", "TSLA", "META", "AMZN", "MSFT"]

def fetch_sentiment(row):
    t       = row["ticker"]
    social  = client.sentiment.social(t)
    news    = client.sentiment.news(t)
    analyst = client.sentiment.analyst(t)
    return (t, {
        "ticker":  t,
        "social":  social["score"],
        "news":    news["score"],
        "analyst": analyst["score"],
    })

df_tickers = utils.df([{"ticker": t} for t in tickers])
signal_map = {}
utils.work(df_tickers, fetch_sentiment, signal_map)

df_signals = utils.df(list(signal_map.values()))

visualize.scatter(
    df_signals,
    x="social",
    y="analyst",
    hover=["ticker", "news"],
)
`,
    },
    {
      label: 'News Sentiment Ranking',
      language: 'python',
      filename: 'news_sentiment_ranking.py',
      bg: '8.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers = ["XOM", "CVX", "BP", "SHEL", "COP", "SLB", "MPC"]

def fetch_news_sentiment(row):
    t    = row["ticker"]
    news = client.sentiment.news(t)
    return (t, {
        "ticker":   t,
        "score":    news["score"],
        "articles": news["articles"],
    })

df_tickers   = utils.df([{"ticker": t} for t in tickers])
news_map     = {}
utils.work(df_tickers, fetch_news_sentiment, news_map)

df_news  = utils.df(list(news_map.values()))
df_news  = df_news.sort_values("score", ascending=False)

visualize.barh(df_news, x="score", y="ticker")
`,
    },
    {
      label: 'Crowd vs. Analyst',
      language: 'python',
      filename: 'crowd_vs_analyst.py',
      bg: '4.png',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers = ["TSLA", "GME", "AMC", "PLTR", "RIVN", "SOFI"]

def fetch_divergence(row):
    t      = row["ticker"]
    social = client.sentiment.social(t)
    analyst = client.sentiment.analyst(t)

    buy_ratio = (
        (analyst["strongBuy"] + analyst["buy"]) /
        max(analyst["strongBuy"] + analyst["buy"] + analyst["hold"] + analyst["sell"], 1)
    )

    return (t, {
        "ticker":      t,
        "crowd":       social["score"],
        "analyst":     analyst["score"],
        "buy_ratio":   buy_ratio,
        "divergence":  social["score"] - analyst["score"],
    })

df_tickers  = utils.df([{"ticker": t} for t in tickers])
div_map     = {}
utils.work(df_tickers, fetch_divergence, div_map)

df_div  = utils.df(list(div_map.values()))
df_div  = df_div.sort_values("divergence", ascending=False)

visualize.bar(df_div, x="ticker", y="divergence")
`,
    },
    {
      label: 'Sentiment vs. Fundamentals',
      language: 'python',
      filename: 'sentiment_vs_fundamentals.py',
      bg: '1.jpeg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers = ["AAPL", "MSFT", "GOOGL", "META", "AMZN", "NVDA"]

def fetch_combined(row):
    t       = row["ticker"]
    news    = client.sentiment.news(t)
    metrics = client.financials.metrics(t)

    return (t, {
        "ticker":       t,
        "news_score":   news["score"],
        "gross_margin": metrics["grossMargin"],
        "roe":          metrics["returnOnEquity"],
        "debt_equity":  metrics["debtToEquity"],
    })

df_tickers   = utils.df([{"ticker": t} for t in tickers])
combined_map = {}
utils.work(df_tickers, fetch_combined, combined_map)

df_combined = utils.df(list(combined_map.values()))

visualize.fit(
    df_combined,
    x="news_score",
    y="roe",
    hover=["ticker", "gross_margin"],
)
`,
    },
  ]}
/>
      <Marquee />

      <FeaturesGrid
        title="Market Sentiment. "
        subtitle="Sentiment analysis API data from news, social media, and analyst reports. "
        features={[
          {
            fig: 'FIG 0.1',
            icon: <Globe size={80} color="var(--purple)" />,
            title: 'News Sentiment',
            description:
              'Current news sentiment analysis and scoring from 500+ financial news sources with entity-level granularity. Filter by ticker, industry, or region for targeted analysis.',
          },
          {
            fig: 'FIG 0.2',
            icon: <MessageSquare size={80} color="var(--green)" />,
            title: 'Social Media',
            description:
              'Track stock sentiment from Twitter, Reddit, StockTwits, and other social platforms. Capture crowd psychology and retail-driven momentum before it hits the tape.',
          },
          {
            fig: 'FIG 0.3',
            icon: <Phone size={80} color="var(--red)" />,
            title: 'Earnings Calls',
            description:
              'Analyze management tone and sentiment from earnings call transcripts using NLP. Detect hesitation, optimism, and subtle shifts in language patterns.',
          },
          {
            fig: 'FIG 0.4',
            icon: <Zap size={80} color="var(--purple)" />,
            title: 'Daily Sentiment Signals',
            description:
              'Track daily sentiment signals for tracked securities. Integrate into research workflows, dashboards, or risk models, with decades of history for backtesting.',
          },
          {
            fig: 'FIG 0.5',
            icon: <Tag size={80} color="var(--green)" />,
            title: 'Entity Extraction',
            description:
              'Precision sentiment tagging for companies, executives, products, and geopolitical events. Isolate the signal relevant to your specific holdings or watchlist.',
          },
          {
            fig: 'FIG 0.6',
            icon: <TrendingUp size={80} color="var(--red)" />,
            title: 'Momentum & Anomaly',
            description:
              'Identify abnormal spikes in volume, sentiment divergence, and emerging narratives. Surface the sentiment breaks that have historically preceded price moves.',
          },
        ]}
      />

      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Global Sentiment Coverage"
        headingPlain="Sentiment data"
        headingEm="across 500+ sources in 40+ languages."
        body="Aggregate sentiment data from financial news, social chatter, and analyst reports across every major market - normalized, scored, and refreshed daily."
        stats={[
          { v: '500+', l: 'News Sources' },
          { v: '10M+', l: 'Social Posts Daily' },
          { v: '1M+', l: 'Articles Processed' },
          { v: '40+', l: 'Languages' },
        ]}
        markers={[
          { id: 'nyc', location: [40.71, -74.01], label: 'NYC' },
          { id: 'london', location: [51.51, -0.09], label: 'London' },
          { id: 'tokyo', location: [35.68, 139.77], label: 'Tokyo' },
          { id: 'hongkong', location: [22.28, 114.16], label: 'Hong Kong' },
          { id: 'singapore', location: [1.35, 103.82], label: 'Singapore' },

          { id: 'sanfrancisco', location: [37.77, -122.42], label: 'San Francisco' },
          { id: 'losangeles', location: [34.05, -118.24], label: 'Los Angeles' },
          { id: 'chicago', location: [41.88, -87.63], label: 'Chicago' },
          { id: 'toronto', location: [43.65, -79.38], label: 'Toronto' },
          { id: 'vancouver', location: [49.28, -123.12], label: 'Vancouver' },

          { id: 'newdelhi', location: [28.61, 77.21], label: 'New Delhi' },
          { id: 'mumbai', location: [19.07, 72.87], label: 'Mumbai' },
          { id: 'seoul', location: [37.56, 126.98], label: 'Seoul' },
          { id: 'beijing', location: [39.90, 116.40], label: 'Beijing' },
          { id: 'shanghai', location: [31.23, 121.47], label: 'Shanghai' },

          { id: 'sydney', location: [-33.87, 151.21], label: 'Sydney' },
          { id: 'melbourne', location: [-37.81, 144.96], label: 'Melbourne' },
          { id: 'auckland', location: [-36.85, 174.76], label: 'Auckland' },

          { id: 'paris', location: [48.86, 2.35], label: 'Paris' },
          { id: 'frankfurt', location: [50.11, 8.68], label: 'Frankfurt' },
          { id: 'berlin', location: [52.52, 13.40], label: 'Berlin' },
          { id: 'zurich', location: [47.37, 8.54], label: 'Zurich' },
          { id: 'amsterdam', location: [52.37, 4.90], label: 'Amsterdam' },
          { id: 'madrid', location: [40.42, -3.70], label: 'Madrid' },
          { id: 'rome', location: [41.90, 12.49], label: 'Rome' },
          { id: 'stockholm', location: [59.33, 18.07], label: 'Stockholm' },

          { id: 'dubai', location: [25.20, 55.27], label: 'Dubai' },
          { id: 'riyadh', location: [24.71, 46.67], label: 'Riyadh' },
          { id: 'telaviv', location: [32.08, 34.78], label: 'Tel Aviv' },
          { id: 'istanbul', location: [41.01, 28.97], label: 'Istanbul' },

          { id: 'saopaulo', location: [-23.55, -46.63], label: 'São Paulo' },
          { id: 'mexicocity', location: [19.43, -99.13], label: 'Mexico City' },
          { id: 'buenosaires', location: [-34.60, -58.38], label: 'Buenos Aires' },
          { id: 'santiago', location: [-33.45, -70.66], label: 'Santiago' },

          { id: 'johannesburg', location: [-26.20, 28.04], label: 'Johannesburg' },
          { id: 'lagos', location: [6.52, 3.38], label: 'Lagos' },
          { id: 'nairobi', location: [-1.29, 36.82], label: 'Nairobi' },
          { id: 'cairo', location: [30.04, 31.24], label: 'Cairo' },

          { id: 'moscow', location: [55.75, 37.62], label: 'Moscow' },
          { id: 'warsaw', location: [52.23, 21.01], label: 'Warsaw' },
          { id: 'prague', location: [50.08, 14.43], label: 'Prague' },
          { id: 'vienna', location: [48.21, 16.37], label: 'Vienna' },

          { id: 'taipei', location: [25.03, 121.56], label: 'Taipei' },
          { id: 'bangkok', location: [13.75, 100.50], label: 'Bangkok' },
          { id: 'jakarta', location: [-6.20, 106.85], label: 'Jakarta' },
          { id: 'manila', location: [14.60, 120.98], label: 'Manila' },
          { id: 'kualalumpur', location: [3.14, 101.69], label: 'Kuala Lumpur' },

          { id: 'oslo', location: [59.91, 10.75], label: 'Oslo' },
          { id: 'helsinki', location: [60.17, 24.94], label: 'Helsinki' },
          { id: 'copenhagen', location: [55.68, 12.57], label: 'Copenhagen' },

          { id: 'washington', location: [38.90, -77.04], label: 'Washington DC' },
          { id: 'boston', location: [42.36, -71.06], label: 'Boston' },
          { id: 'atlanta', location: [33.75, -84.39], label: 'Atlanta' },
          { id: 'dallas', location: [32.78, -96.80], label: 'Dallas' },
          { id: 'houston', location: [29.76, -95.37], label: 'Houston' },
        ]}
      />
      <DataGrid
        title="Market Sentiment API: Quantify the Narrative"
        subtitle="Transform unstructured news, social chatter, filings, and analyst reports into actionable sentiment data and quantitative signals. Track market mood, momentum shifts, and controversy risk with current daily signals and decades of history."
        bg={databg}
        stats={[
          { label: 'Sources Monitored', value: '500+' },
          { label: 'Articles Processed Daily', value: '5M+' },
          { label: 'Sentiment Metrics', value: '15+' },
        ]}
      />
      <CardList
        title="Measure Market Emotion with Sentiment Analysis"
        vid={'https://cdn.studio14a.com/axion/products/cards/senti.mp4'}
        points={[
          {
            "title": "Multi-Source Sentiment Aggregation",
            "details": "Consolidated sentiment scores from financial news, social media, SEC filings, earnings call transcripts, and broker research. Avoid single-source bias with our aggregated market sentiment engine.",
            "cta": "Explore sentiment sources",
            "url": "/docs"
          },
          {
            "title": "Entity-Specific Signal Extraction",
            "details": "Precision sentiment tagging for companies, executives, products, and geopolitical events. Isolate the signal relevant to your specific holdings or watchlist from the global noise.",
            "cta": "View entity analysis",
            "url": "/products/news"
          },
          {
            "title": "Momentum & Anomaly Detection",
            "details": "Identify abnormal spikes in volume, sentiment divergence, and emerging narratives. Get alerts on sentiment breaks that have historically preceded price moves.",
            "cta": "Set up sentiment alerts",
            "url": "/docs"
          }
        ]}
      />
        <Responses
        heading="A single sentiment score for every ticker and conversation"
        subheading="From Reddit threads to research desks, see exactly how the world is talking about AAPL - broken down by source, scored for confidence, and updated daily."
        ticker="AAPL"
        tabs={[
          {
            title: 'Social Media',
            description: 'Current pulse from Google search trends, Twitter/X conversations, and Reddit discussions about AAPL.',
            data: {
              label: 'NEGATIVE',
              score: 0.99506558974584,
              breakdown: {
                positive: { count: 145, avgScore: 0.93158021569252 },
                negative: { count: 436, avgScore: 0.99506558974584 },
              },
            },
          },
          {
            title: 'News',
            description: 'How financial media is covering AAPL - earnings, product launches, and corporate developments.',
            data: {
              label: 'NEGATIVE',
              score: 0.982357367873192,
              breakdown: {
                positive: { count: 231, avgScore: 0.903340172767639 },
                negative: { count: 1292, avgScore: 0.982357367873192 },
              },
            },
          },
          {
            title: 'Filing Sentiment',
            description: 'Aggregated sentiment and key-topic breakdown for a company\'s recent SEC filings.',
            data: {
              "label": "NEGATIVE",
              "score": 0.984021182571139,
              "breakdown": {
                "positive": {
                  "count": 63,
                  "avgScore": 0.987096269925435
                },
                "negative": {
                  "count": 293,
                  "avgScore": 0.984021182571139
                }
              }
            },
          },
          {
            title: 'Earnings Calls Sentiment',
            description: 'Access full quarterly earnings call transcripts with sentiment analysis.',
            data:
            {
              "label": "POSITIVE",
              "score": 0.9626,
              "breakdown": {
                "positive": {
                  "count": 5063,
                  "avgScore": 0.9626
                },
                "negative": {
                  "count": 1878,
                  "avgScore": 0.9698
                }
              }
            }
          },


          {
            title: 'Analyst Reports',
            description: 'Sentiment from professional analyst research notes and investment recommendations on AAPL.',
            data: {
              sentiment: 'NEGATIVE',
              score: 0.31,
            },
          },
        ]}
      />

      <DatasetDetails full={true}
        title="Comprehensive Market Sentiment Data Coverage"
        subtitle="Current sentiment analysis from 500+ financial news sources with entity-level granularity. Track market sentiment across social platforms, analyze management tone from earnings call transcripts using NLP, and surface sentiment shifts across decades of history."
        cta="Get Started Today"
        ctaLink="/login"
        tiles={[
          {
            img: '7.webp',
            title: 'We analyze 500+ news sources',
            caption: 'You can track sentiment from financial news, social media, and earnings calls with daily updates.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '13.webp',
            title: 'We process 1M+ articles daily',
            caption: 'You never miss a market-moving story with our massive content ingestion pipeline.',
            cn: 'scaled',
            tile: <W2/>
          },
          {
            img: '2.webp',
            title: 'We track 10M+ social posts',
            caption: 'You can monitor Twitter, Reddit, and StockTwits for crowd-sourced sentiment signals.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '5.webp',
            title: 'We support 40+ languages',
            caption: 'You get a truly global view of market narrative with multilingual sentiment analysis.',
            cn: 'scaled',
            tile: <W4/>
          },
          {
            img: '10.webp',
            title: 'We refresh sentiment daily',
            caption: 'You can track how sentiment shifts across days, quarters, and years for your tracked securities.',
            cn: 'scaled corner',
            tile: <W5/>
          }
        ]}
      />

      <Outcomes
        headline="Numbers that speak for themselves."
        leftCell={{
          stat: '87%',
          title: 'Correlation with 30-day volatility.',
          desc: 'Sentiment extremes foreshadow volatility expansion.',
          showChart: true,
        }}
        rightCells={[
          {
            stat: '3.2s',
            title: 'Average retrieval latency.',
            desc: 'Decades of history pulled in seconds.',
          },
          {
            stat: '99.9%',
            title: 'Data delivery SLA.',
            desc: 'Reliability you can count on, even during market turmoil.',
            boldDesc: true,
          },
        ]}
      /><ProductFAQ slug="sentiment" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default SentimentPage;
